import * as React from "react";
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
  type Algorithm,
  type AlgorithmInput,
  type AlgorithmOutput,
} from "@googlemaps/markerclusterer";

import { createStopTooltipOverlay } from "./tooltips/stopTooltipOverlay";

export type ClusterStop = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  type?: string;
  servedLineIds?: string[];
  bytesprio?: number;
};

type StopIconState = {
  hovered?: boolean;
  focused?: boolean;
  focusedColor?: string;
};

type Props = {
  map: google.maps.Map | null;
  stops: ClusterStop[];
  activeStopId?: string | null;
  getStopIcon: (
    stop: ClusterStop,
    state?: StopIconState
  ) => google.maps.Icon | string;
  getClusterIcon: (
    count: number,
    dimmed: boolean,
    hovered?: boolean
  ) => google.maps.Icon | string;
  onStopClick?: (stop: ClusterStop) => void;
  dimmed?: boolean;
  maxClusterZoom?: number;
  iconRefreshKey?: string | number | null;
  focusedStopColor?: string | null;
  focusedStopRootIds?: Set<string> | null;
  forceVisibleStopRootIds?: Set<string> | null;
  dimmedOpacity?: number;

  trackVisibleSingletons?: boolean;
  onVisibleSingletonsChange?: (ids: Set<string>) => void;

  recomputeDebounceMs?: number;
  repaintDebounceMs?: number;

  clusterRadius?: number;
  minClusterPoints?: number;

  showClusterSingletonsFromZoom?: number;
  minIndividualBytesprio?: number;
};

const rootIdOf = (id: string) => String(id).split("-")[0];
class ViewportSingletonAlgorithm implements Algorithm {
  constructor(
    private readonly delegate: Algorithm,
    private readonly showSingletonsFromZoom: number,
    private readonly paddingRatio = 0.2
  ) {}

  calculate(input: AlgorithmInput): AlgorithmOutput {
    const bounds = input.map.getBounds();

    if (!bounds) {
      return {
        clusters: [],
        changed: true,
      };
    }

    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();

    const latPadding = (northEast.lat() - southWest.lat()) * this.paddingRatio;

    const lngPadding = (northEast.lng() - southWest.lng()) * this.paddingRatio;

    const paddedBounds = new google.maps.LatLngBounds(
      {
        lat: southWest.lat() - latPadding,
        lng: southWest.lng() - lngPadding,
      },
      {
        lat: northEast.lat() + latPadding,
        lng: northEast.lng() + lngPadding,
      }
    );

    const viewportMarkers = input.markers.filter((marker) => {
      const position = (marker as google.maps.Marker).getPosition();

      return position ? paddedBounds.contains(position) : false;
    });

    const result = this.delegate.calculate({
      ...input,
      markers: viewportMarkers,
    });

    const zoom = input.map.getZoom() ?? 0;

    if (zoom >= this.showSingletonsFromZoom) {
      return result;
    }

    return {
      ...result,
      clusters: result.clusters.filter(
        (cluster) => (cluster.markers?.length ?? 0) > 1
      ),
    };
  }
}

export default function ClusterManager({
  map,
  stops,
  activeStopId = null,
  getStopIcon,
  getClusterIcon,
  onStopClick,
  dimmed = false,
  maxClusterZoom = 18,
  iconRefreshKey,
  focusedStopColor = null,
  focusedStopRootIds = null,
  forceVisibleStopRootIds = null,
  dimmedOpacity = 0.5,

  trackVisibleSingletons = false,
  onVisibleSingletonsChange,

  recomputeDebounceMs = 150,
  repaintDebounceMs = 80,

  clusterRadius = 200,
  minClusterPoints = 2,
  minIndividualBytesprio = 0,
  showClusterSingletonsFromZoom = 15,
}: Props) {
  const clustererRef = React.useRef<MarkerClusterer | null>(null);
  const markersRef = React.useRef<google.maps.Marker[]>([]);
  const hoveredStopIdRef = React.useRef<string | null>(null);

  const repaintTimeout = React.useRef<number | null>(null);
  const recomputeTimeout = React.useRef<number | null>(null);

  const getStopIconRef = React.useRef(getStopIcon);
  const getClusterIconRef = React.useRef(getClusterIcon);
  const onStopClickRef = React.useRef(onStopClick);
  const onVisibleSingletonsChangeRef = React.useRef(onVisibleSingletonsChange);

  React.useEffect(() => {
    getStopIconRef.current = getStopIcon;
  }, [getStopIcon, focusedStopColor]);

  React.useEffect(() => {
    getClusterIconRef.current = getClusterIcon;
  }, [getClusterIcon]);

  React.useEffect(() => {
    onStopClickRef.current = onStopClick;
  }, [onStopClick]);

  React.useEffect(() => {
    onVisibleSingletonsChangeRef.current = onVisibleSingletonsChange;
  }, [onVisibleSingletonsChange]);

  const stopsById = React.useMemo(() => {
    const map = new Map<string, ClusterStop>();
    for (const s of stops) map.set(String(s.id), s);
    return map;
  }, [stops]);

  const stopsKey = React.useMemo(() => {
    let acc = 0;
    for (const s of stops) {
      acc = (acc * 31 + String(s.id).length) | 0;
    }
    return `${stops.length}:${acc}`;
  }, [stops]);

  const isActiveStop = React.useCallback(
    (stopId: string) => {
      if (!activeStopId) return false;
      return rootIdOf(String(activeStopId)) === rootIdOf(stopId);
    },
    [activeStopId]
  );

  const isStopFocused = React.useCallback(
    (stopId: string) => {
      if (!focusedStopRootIds) return true;
      return focusedStopRootIds.has(rootIdOf(stopId));
    },
    [focusedStopRootIds]
  );

  const isForceVisibleStop = React.useCallback(
    (stopId: string) => {
      if (!forceVisibleStopRootIds) return false;
      return forceVisibleStopRootIds.has(rootIdOf(stopId));
    },
    [forceVisibleStopRootIds]
  );

  const markerOpacityFor = React.useCallback(
    (stopId: string, hovered: boolean) => {
      if (hovered) return 1;
      if (isActiveStop(stopId)) return 1;
      if (isForceVisibleStop(stopId)) return 1;
      if (isStopFocused(stopId)) return 1;

      return dimmedOpacity;
    },
    [isActiveStop, isForceVisibleStop, isStopFocused, dimmedOpacity]
  );

  const markerZIndexFor = React.useCallback(
    (stopId: string, hovered: boolean) => {
      if (hovered) return 3000;
      if (isActiveStop(stopId)) return 3000;
      if (isForceVisibleStop(stopId)) return 2500;
      if (isStopFocused(stopId)) return 2;

      return 1;
    },
    [isActiveStop, isForceVisibleStop, isStopFocused]
  );

  const repaintClusters = React.useCallback(
    (delay = repaintDebounceMs) => {
      if (repaintTimeout.current) {
        window.clearTimeout(repaintTimeout.current);
      }

      repaintTimeout.current = window.setTimeout(() => {
        repaintTimeout.current = null;
        (clustererRef.current as any)?.repaint?.();
      }, delay);
    },
    [repaintDebounceMs]
  );

  const recomputeVisibleSingletons = React.useCallback(() => {
    const cb = onVisibleSingletonsChangeRef.current;
    if (!cb || !map) return;

    const bounds = map.getBounds();
    if (!bounds) {
      cb(new Set());
      return;
    }

    const ids = new Set<string>();

    for (const marker of markersRef.current) {
      if (!marker.getMap()) continue;

      const pos = marker.getPosition();
      if (!pos || !bounds.contains(pos)) continue;

      const raw = marker.get("stopId");
      if (raw != null) ids.add(String(raw));
    }

    cb(ids);
  }, [map]);

  const scheduleRecomputeVisibleSingletons = React.useCallback(
    (delay = recomputeDebounceMs) => {
      if (!trackVisibleSingletons) return;
      if (!onVisibleSingletonsChangeRef.current) return;
      if (!map) return;

      if (recomputeTimeout.current) {
        window.clearTimeout(recomputeTimeout.current);
      }

      recomputeTimeout.current = window.setTimeout(() => {
        recomputeTimeout.current = null;
        recomputeVisibleSingletons();
      }, delay);
    },
    [
      trackVisibleSingletons,
      recomputeDebounceMs,
      recomputeVisibleSingletons,
      map,
    ]
  );

  React.useEffect(() => {
    if (!map) return;

    for (const marker of markersRef.current) {
      marker.setMap(null);
    }

    markersRef.current = [];
    hoveredStopIdRef.current = null;

    const tooltip = createStopTooltipOverlay();

    const localMarkers: google.maps.Marker[] = [];
    const clusterableMarkers: google.maps.Marker[] = [];

    for (const stop of stops) {
      const stopId = String(stop.id);
      const active = isActiveStop(stopId);
      const focused = isStopFocused(stopId);
      const forceVisible = isForceVisibleStop(stopId);

      const priorityVisible = (stop.bytesprio ?? 0) >= minIndividualBytesprio;

      const individuallyVisible = active || forceVisible || priorityVisible;
      const marker = new google.maps.Marker({
        position: { lat: stop.lat, lng: stop.lng },
        map: individuallyVisible ? map : undefined,
        icon: getStopIconRef.current(stop, {
          hovered: active,
          focused: forceVisible || focused,
          focusedColor:
            forceVisible || focused ? focusedStopColor ?? undefined : undefined,
        }),

        optimized: true,
        opacity: markerOpacityFor(stopId, false),
        zIndex: markerZIndexFor(stopId, false),
      });

      marker.set("stopId", stopId);

      marker.addListener("mouseover", () => {
        hoveredStopIdRef.current = stopId;

        const focusedNow = isStopFocused(stopId);
        const forceVisibleNow = isForceVisibleStop(stopId);

        marker.setIcon(
          getStopIconRef.current(stop, {
            hovered: true,
            focused: forceVisibleNow || focusedNow,
            focusedColor:
              forceVisibleNow || focusedNow
                ? focusedStopColor ?? undefined
                : undefined,
          })
        );

        marker.setOpacity(1);
        marker.setZIndex(3000);
        document.body.style.cursor = "pointer";

        tooltip.show(map, { lat: stop.lat, lng: stop.lng }, stop.name);
      });

      marker.addListener("mouseout", () => {
        hoveredStopIdRef.current = null;

        const activeNow = isActiveStop(stopId);
        const focusedNow = isStopFocused(stopId);
        const forceVisibleNow = isForceVisibleStop(stopId);
        marker.setIcon(
          getStopIconRef.current(stop, {
            hovered: activeNow,
            focused: forceVisibleNow || focusedNow,
            focusedColor:
              forceVisibleNow || focusedNow
                ? focusedStopColor ?? undefined
                : undefined,
          })
        );
        marker.setOpacity(markerOpacityFor(stopId, false));
        marker.setZIndex(markerZIndexFor(stopId, false));
        document.body.style.cursor = "";

        if (!activeNow) {
          tooltip.hide();
        }
      });

      if (onStopClickRef.current) {
        marker.addListener("click", () => {
          onStopClickRef.current?.(stop);
          tooltip.show(map, { lat: stop.lat, lng: stop.lng }, stop.name);
        });
      }

      localMarkers.push(marker);

      if (!individuallyVisible) {
        clusterableMarkers.push(marker);
      }
    }

    markersRef.current = localMarkers;

    const activeStop = activeStopId
      ? stops.find(
          (stop) => rootIdOf(String(stop.id)) === rootIdOf(String(activeStopId))
        )
      : null;
    if (activeStop) {
      tooltip.show(
        map,
        { lat: activeStop.lat, lng: activeStop.lng },
        activeStop.name
      );
    }

    clustererRef.current?.clearMarkers();

    clustererRef.current?.setMap(null);
    clustererRef.current?.clearMarkers(true);
    clustererRef.current = null;

    const clusterer = new MarkerClusterer({
      map,
      markers: clusterableMarkers,

      onClusterClick: (_event, cluster, clickedMap) => {
        const zoomBefore = clickedMap.getZoom() ?? 0;

        const idleListener = google.maps.event.addListenerOnce(
          clickedMap,
          "idle",
          () => {
            const zoomAfter = clickedMap.getZoom() ?? zoomBefore;

            if (zoomAfter <= zoomBefore) {
              clickedMap.panTo(cluster.position);
              clickedMap.setZoom(Math.min(zoomBefore + 1, maxClusterZoom + 1));
            }
          }
        );

        if (cluster.bounds) {
          clickedMap.fitBounds(cluster.bounds);
        } else {
          google.maps.event.removeListener(idleListener);

          clickedMap.panTo(cluster.position);
          clickedMap.setZoom(Math.min(zoomBefore + 1, maxClusterZoom + 1));
        }
      },

      algorithm: new ViewportSingletonAlgorithm(
        new SuperClusterAlgorithm({
          radius: clusterRadius,
          maxZoom: maxClusterZoom,
          minPoints: minClusterPoints,
        }),
        showClusterSingletonsFromZoom,
        0.2
      ),

      renderer: {
        render: (cluster) => {
          const firstMarker = cluster.markers?.[0] as
            | google.maps.Marker
            | undefined;
          const firstStopId = firstMarker?.get("stopId");
          const firstStop =
            firstStopId != null
              ? stopsById.get(String(firstStopId))
              : undefined;

          const fallbackStop: ClusterStop = firstStop ?? {
            id: `cluster-${cluster.count}`,
            name: "Hållplatser",
            lat: cluster.position.lat(),
            lng: cluster.position.lng(),
          };

          const clusterRootId = rootIdOf(fallbackStop.id);

          const clusterFocused =
            !focusedStopRootIds || focusedStopRootIds.has(clusterRootId);

          const clusterDimmed = dimmed || !clusterFocused;

          const label: google.maps.MarkerLabel = {
            text: String(cluster.count),
            color: clusterDimmed ? "rgba(50, 0, 16, 0.5)" : "#320010",
            fontSize: "14px",
            fontWeight: "700",
          };

          const marker = new google.maps.Marker({
            position: cluster.position,
            icon: getClusterIconRef.current(
              cluster.count,
              clusterDimmed,
              false
            ),
            label,
            optimized: true,
            zIndex: 1000 + cluster.count,
          });

          marker.addListener("mouseover", () => {
            marker.setIcon(
              getClusterIconRef.current(cluster.count, false, true)
            );

            marker.setLabel({
              ...label,
              color: "#320010",
            });

            marker.setZIndex(4000 + cluster.count);
            document.body.style.cursor = "pointer";
          });

          marker.addListener("mouseout", () => {
            marker.setIcon(
              getClusterIconRef.current(cluster.count, clusterDimmed, false)
            );

            marker.setLabel(label);
            marker.setZIndex(1000 + cluster.count);
            document.body.style.cursor = "";
          });

          return marker;
        },
      },
    });
    clustererRef.current = clusterer;

    repaintClusters(0);
    scheduleRecomputeVisibleSingletons(0);

    return () => {
      tooltip.hide();

      clusterer.setMap(null);
      clusterer.clearMarkers(true);

      if (clustererRef.current === clusterer) {
        clustererRef.current = null;
      }

      for (const marker of localMarkers) {
        google.maps.event.clearInstanceListeners(marker);
        marker.setMap(null);
      }

      if (repaintTimeout.current) {
        window.clearTimeout(repaintTimeout.current);
      }

      if (recomputeTimeout.current) {
        window.clearTimeout(recomputeTimeout.current);
      }

      repaintTimeout.current = null;
      recomputeTimeout.current = null;
      hoveredStopIdRef.current = null;
      document.body.style.cursor = "";
    };
  }, [
    map,
    stopsKey,
    stops,
    activeStopId,
    focusedStopRootIds,
    forceVisibleStopRootIds,
    focusedStopColor,
    dimmed,
    maxClusterZoom,
    clusterRadius,
    minClusterPoints,
    isActiveStop,
    isStopFocused,
    isForceVisibleStop,
    markerOpacityFor,
    markerZIndexFor,
    repaintClusters,
    scheduleRecomputeVisibleSingletons,
    minIndividualBytesprio,
    showClusterSingletonsFromZoom,
  ]);

  React.useEffect(() => {
    for (const marker of markersRef.current) {
      const stopId = String(marker.get("stopId"));
      const stop = stopsById.get(stopId);
      if (!stop) continue;

      const hovered = hoveredStopIdRef.current === stopId;
      const active = isActiveStop(stopId);
      const focused = isStopFocused(stopId);
      const forceVisible = isForceVisibleStop(stopId);
      const highlighted = hovered || active;

      marker.setIcon(
        getStopIconRef.current(stop, {
          hovered: highlighted,
          focused: forceVisible || focused,
          focusedColor:
            forceVisible || focused ? focusedStopColor ?? undefined : undefined,
        })
      );

      marker.setOpacity(markerOpacityFor(stopId, hovered));
      marker.setZIndex(markerZIndexFor(stopId, hovered));
    }

    repaintClusters();
  }, [
    iconRefreshKey,
    activeStopId,
    focusedStopRootIds,
    forceVisibleStopRootIds,
    dimmedOpacity,
    stopsById,
    isActiveStop,
    isStopFocused,
    isForceVisibleStop,
    markerOpacityFor,
    markerZIndexFor,
    repaintClusters,
    focusedStopColor,
  ]);

  React.useEffect(() => {
    if (!map) return;
    if (!trackVisibleSingletons) return;
    if (!onVisibleSingletonsChangeRef.current) return;

    const idle = google.maps.event.addListener(map, "idle", () => {
      scheduleRecomputeVisibleSingletons();
    });

    return () => google.maps.event.removeListener(idle);
  }, [map, trackVisibleSingletons, scheduleRecomputeVisibleSingletons]);

  React.useEffect(() => {
    if (!map) return;
    if (!trackVisibleSingletons) return;
    if (!onVisibleSingletonsChangeRef.current) return;

    scheduleRecomputeVisibleSingletons(0);
  }, [map, trackVisibleSingletons, scheduleRecomputeVisibleSingletons]);

  return null;
}
